/**
 * Friction Management UI
 * 
 * Interface for applying regulatory frameworks and entity/relationship frictions
 * Requirements: 3.1-3.7, 3A.1-3A.9, 3B.1-3B.7
 */

import React, { useState, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { logger } from '../../utils/logger';
import { ExpandableList, ExpandableListItem } from './ExpandableList';
import { FrictionListView } from './FrictionListView';
import { FrictionItem } from './FrameworkGroup';

interface FrictionManagementUIProps {
  analysisId: string;
}

export interface FrictionManagementUIRef {
  refreshFrictions: () => Promise<void>;
}

/**
 * Friction Management UI Component
 * 
 * Complete implementation with:
 * - Regulatory framework selection and application
 * - Entity friction creation with logic formulas
 * - Formula syntax validation
 * - Custom friction type definition
 * - API integration
 */
export const FrictionManagementUI = React.forwardRef<FrictionManagementUIRef, FrictionManagementUIProps>(({ analysisId }, ref) => {
  const [activeTab, setActiveTab] = useState<'framework' | 'entity' | 'relationship'>('framework');
  const [appliedFrictions, setAppliedFrictions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Collapsible form state
  const [showEntityForm, setShowEntityForm] = useState(false);
  const [showRelationshipForm, setShowRelationshipForm] = useState(false);
  
  // Dynamically extract unique frameworks from existing frictions
  const availableFrameworks = React.useMemo(() => {
    const frameworks = new Set<string>();
    appliedFrictions.forEach(friction => {
      if (friction.regulatoryFramework) {
        frameworks.add(friction.regulatoryFramework);
      }
    });
    return Array.from(frameworks).sort();
  }, [appliedFrictions]);

  // Expandable list state and cache
  const [affectedItemsCache, setAffectedItemsCache] = useState<Map<string, { entities?: ExpandableListItem[], relationships?: ExpandableListItem[], count: number }>>(new Map());
  const [loadingAffectedItems, setLoadingAffectedItems] = useState<Set<string>>(new Set());

  // Regulatory framework state (deprecated - keeping for backwards compatibility)
  const [selectedFramework, setSelectedFramework] = useState('');
  const [_affectedEntities, _setAffectedEntities] = useState<any[]>([]);

  // Entity friction state (formerly Sentiment Bias)
  const [entityFrictionForm, setEntityFrictionForm] = useState({
    name: '',
    direction: 'negative' as 'negative' | 'positive',
    magnitude: 5,
    logicFormula: '',
    mode: 'simple' as 'simple' | 'advanced',
    baselineTension: 5,
    multiplier: 1.5,
    regulatoryFramework: '' as string,
  });
  const [formulaValid, setFormulaValid] = useState(true);
  const [matchingEntities, setMatchingEntities] = useState<any[]>([]);
  const [editingEntityFrictionId, setEditingEntityFrictionId] = useState<string | null>(null);

  // Relationship friction state
  const [relationshipForm, setRelationshipForm] = useState({
    name: '',
    direction: 'negative' as 'negative' | 'positive',
    magnitude: 5,
    logicFormula: '',
    regulatoryFramework: '' as string,
  });
  const [relationshipFormulaValid, setRelationshipFormulaValid] = useState(true);
  const [matchingRelationships, setMatchingRelationships] = useState<any[]>([]);
  const [editingRelationshipId, setEditingRelationshipId] = useState<string | null>(null);

  // Regulatory friction state
  const [regulatoryForm, setRegulatoryForm] = useState({
    name: '',
    direction: 'negative' as 'negative' | 'positive',
    magnitude: 5,
    logicFormula: '',
    targetEntityIds: [] as string[],
  });
  const [regulatoryFormulaValid, setRegulatoryFormulaValid] = useState(true);
  const [matchingRegulatoryEntities, setMatchingRegulatoryEntities] = useState<any[]>([]);
  const [editingRegulatoryId, setEditingRegulatoryId] = useState<string | null>(null);

  const [refreshing, setRefreshing] = useState(false);

  // Expose refresh function to parent via ref
  React.useImperativeHandle(ref, () => ({
    refreshFrictions: handleRefreshFrictions
  }));

  useEffect(() => {
    logger.setContext({ component: 'FrictionManagementUI', analysisId });
    logger.logComponentMount('FrictionManagementUI');
    fetchAppliedFrictions();
    
    // Listen for entity/relationship changes to invalidate cache
    const handleEntityChange = () => {
      logger.info('Entity changed, clearing affected items cache');
      setAffectedItemsCache(new Map());
    };

    const handleRelationshipChange = () => {
      logger.info('Relationship changed, clearing affected items cache');
      setAffectedItemsCache(new Map());
    };

    const handleFrictionChange = () => {
      logger.info('Friction changed, clearing affected items cache and refetching');
      setAffectedItemsCache(new Map());
      fetchAppliedFrictions();
    };

    window.addEventListener('entityCreated', handleEntityChange);
    window.addEventListener('entityUpdated', handleEntityChange);
    window.addEventListener('entityDeleted', handleEntityChange);
    window.addEventListener('relationshipCreated', handleRelationshipChange);
    window.addEventListener('relationshipUpdated', handleRelationshipChange);
    window.addEventListener('relationshipDeleted', handleRelationshipChange);
    window.addEventListener('frictionUpdated', handleFrictionChange);
    
    return () => {
      logger.logComponentUnmount('FrictionManagementUI');
      logger.clearContext();
      window.removeEventListener('entityCreated', handleEntityChange);
      window.removeEventListener('entityUpdated', handleEntityChange);
      window.removeEventListener('entityDeleted', handleEntityChange);
      window.removeEventListener('relationshipCreated', handleRelationshipChange);
      window.removeEventListener('relationshipUpdated', handleRelationshipChange);
      window.removeEventListener('relationshipDeleted', handleRelationshipChange);
      window.removeEventListener('frictionUpdated', handleFrictionChange);
    };
  }, [analysisId]);

  // Pre-fetch counts when frictions change
  useEffect(() => {
    if (appliedFrictions.length > 0) {
      fetchAllFrictionCounts();
    }
  }, [appliedFrictions]);

  const fetchAppliedFrictions = async () => {
    try {
      setLoading(true);
      logger.info('Fetching applied frictions', { analysisId });
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        logger.error('No authentication token available');
        throw new Error('Not authenticated');
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const url = `${apiUrl}/analyses/${analysisId}/frictions`;
      
      logger.logApiRequest('GET', url);
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      logger.logApiResponse('GET', url, response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Failed to fetch frictions', null, { 
          status: response.status, 
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Failed to fetch frictions: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('Frictions fetched successfully', { count: data.length });
      setAppliedFrictions(data);
      setError(null);
    } catch (err) {
      logger.error('Error fetching frictions', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch frictions');
      setAppliedFrictions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyRegulatoryFramework = async () => {
    if (!selectedFramework) {
      logger.warn('Attempted to apply regulatory framework without selection');
      alert('Please select a regulatory framework');
      return;
    }

    logger.logUserAction('Apply regulatory framework', { framework: selectedFramework });

    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/frictions/types/regulatory`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          framework: selectedFramework,
        }),
      });
      
      if (!response.ok) throw new Error('Failed to apply regulatory framework');
      
      alert('Regulatory framework applied successfully');
      setSelectedFramework('');
      fetchAppliedFrictions();
    } catch (err) {
      console.error('Error applying framework:', err);
      alert(err instanceof Error ? err.message : 'Failed to apply framework');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEntityFriction = async () => {
    if (!entityFrictionForm.name.trim()) {
      alert('Please enter a name for the entity friction');
      return;
    }
    if (!entityFrictionForm.logicFormula.trim()) {
      alert('Please enter a logic formula');
      return;
    }

    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Calculate baselineTension and multiplier based on mode
      const baselineTension = entityFrictionForm.mode === 'simple' 
        ? entityFrictionForm.magnitude 
        : entityFrictionForm.baselineTension;
      const multiplier = entityFrictionForm.mode === 'simple' 
        ? 1.0 + (entityFrictionForm.magnitude / 10) 
        : entityFrictionForm.multiplier;
      
      if (editingEntityFrictionId) {
        // Update existing entity friction using PUT endpoint
        const response = await fetch(`${apiUrl}/analyses/${analysisId}/frictions/types/entity/${editingEntityFrictionId}`, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: entityFrictionForm.name,
            direction: entityFrictionForm.direction,
            baselineTension,
            multiplier,
            conditions: entityFrictionForm.logicFormula,
            regulatoryFramework: entityFrictionForm.regulatoryFramework || undefined,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error?.message || errorData.message || 'Failed to update entity friction';
          throw new Error(errorMessage);
        }
        
        alert('Entity friction updated successfully');
      } else {
        // Create new entity friction
        const response = await fetch(`${apiUrl}/analyses/${analysisId}/frictions/types/entity`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: entityFrictionForm.name,
            direction: entityFrictionForm.direction,
            baselineTension,
            multiplier,
            conditions: entityFrictionForm.logicFormula,
            regulatoryFramework: entityFrictionForm.regulatoryFramework || undefined,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const errorMessage = errorData.error?.message || errorData.message || 'Failed to create entity friction';
          throw new Error(errorMessage);
        }
        
        alert('Entity friction created successfully');
      }
      
      setEntityFrictionForm({
        name: '',
        direction: 'negative',
        magnitude: 5,
        logicFormula: '',
        mode: 'simple',
        baselineTension: 5,
        multiplier: 1.5,
        regulatoryFramework: '',
      });
      setEditingEntityFrictionId(null);
      setMatchingEntities([]);
      setShowEntityForm(false);
      fetchAppliedFrictions();
    } catch (err) {
      console.error('Error creating/updating entity friction:', err);
      alert(err instanceof Error ? err.message : 'Failed to create/update entity friction');
    } finally {
      setLoading(false);
    }
  };

  const handleEditEntityFriction = (friction: any) => {
    // Detect mode based on whether baselineTension and multiplier exist
    const hasHybridFields = friction.baselineTension !== undefined && 
                           friction.multiplier !== undefined;
    
    let mode: 'simple' | 'advanced' = 'simple';
    let baselineTension = 5;
    let multiplier = 1.5;
    let magnitude = 5;
    
    if (hasHybridFields) {
      baselineTension = friction.baselineTension;
      multiplier = friction.multiplier;
      
      // Check if it follows simple mode formula
      const expectedMultiplier = 1.0 + (baselineTension / 10);
      const isSimpleMode = Math.abs(multiplier - expectedMultiplier) < 0.01;
      
      if (isSimpleMode) {
        mode = 'simple';
        magnitude = baselineTension;
      } else {
        mode = 'advanced';
      }
    } else if (friction.magnitude !== undefined) {
      // Old format with magnitude only
      magnitude = friction.magnitude;
      baselineTension = magnitude;
      multiplier = 1.0 + (magnitude / 10);
      mode = 'simple';
    }
    
    setEntityFrictionForm({
      name: friction.name,
      direction: friction.direction || 'negative',
      magnitude,
      logicFormula: friction.logicFormula || friction.conditions || '',
      mode,
      baselineTension,
      multiplier,
      regulatoryFramework: friction.regulatoryFramework || '',
    });
    setEditingEntityFrictionId(friction.id);
    setFormulaValid(true);
    setShowEntityForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEntityFrictionForm({
      name: '',
      direction: 'negative',
      magnitude: 5,
      logicFormula: '',
      mode: 'simple',
      baselineTension: 5,
      multiplier: 1.5,
      regulatoryFramework: '',
    });
    setEditingEntityFrictionId(null);
    setMatchingEntities([]);
    setShowEntityForm(false);
  };

  const handleCopyEntityFriction = (friction: any) => {
    // Detect mode based on whether baselineTension and multiplier exist
    const hasHybridFields = friction.baselineTension !== undefined && 
                           friction.multiplier !== undefined;
    
    let mode: 'simple' | 'advanced' = 'simple';
    let baselineTension = 5;
    let multiplier = 1.5;
    let magnitude = 5;
    
    if (hasHybridFields) {
      baselineTension = friction.baselineTension;
      multiplier = friction.multiplier;
      
      // Check if it follows simple mode formula
      const expectedMultiplier = 1.0 + (baselineTension / 10);
      const isSimpleMode = Math.abs(multiplier - expectedMultiplier) < 0.01;
      
      if (isSimpleMode) {
        mode = 'simple';
        magnitude = baselineTension;
      } else {
        mode = 'advanced';
      }
    } else if (friction.magnitude !== undefined) {
      // Old format with magnitude only
      magnitude = friction.magnitude;
      baselineTension = magnitude;
      multiplier = 1.0 + (magnitude / 10);
      mode = 'simple';
    }
    
    setEntityFrictionForm({
      name: `${friction.name} (Copy)`,
      direction: friction.direction || 'negative',
      magnitude,
      logicFormula: friction.logicFormula || friction.conditions || '',
      mode,
      baselineTension,
      multiplier,
      regulatoryFramework: friction.regulatoryFramework || '',
    });
    setEditingEntityFrictionId(null); // Clear editing ID so it creates a new one
    setFormulaValid(true);
    setShowEntityForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEditRelationshipFriction = (friction: any) => {
    setRelationshipForm({
      name: friction.name,
      direction: friction.direction || 'negative',
      magnitude: friction.magnitude || 5,
      logicFormula: friction.logicFormula || friction.conditions || '',
      regulatoryFramework: friction.regulatoryFramework || '',
    });
    setEditingRelationshipId(friction.id);
    setRelationshipFormulaValid(true);
    setShowRelationshipForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelRelationshipEdit = () => {
    setRelationshipForm({
      name: '',
      direction: 'negative',
      magnitude: 5,
      logicFormula: '',
      regulatoryFramework: '',
    });
    setEditingRelationshipId(null);
    setMatchingRelationships([]);
  };

  const handleCopyRelationshipFriction = (friction: any) => {
    setRelationshipForm({
      name: `${friction.name} (Copy)`,
      direction: friction.direction || 'negative',
      magnitude: friction.magnitude || 5,
      logicFormula: friction.logicFormula || friction.conditions || '',
      regulatoryFramework: friction.regulatoryFramework || '',
    });
    setEditingRelationshipId(null); // Clear editing ID so it creates a new one
    setRelationshipFormulaValid(true);
    setShowRelationshipForm(true);
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCreateRelationshipFriction = async () => {
    if (!relationshipForm.name.trim()) {
      alert('Please enter a name for the relationship friction');
      return;
    }
    if (!relationshipForm.logicFormula.trim()) {
      alert('Please enter a logic formula');
      return;
    }

    try {
      setLoading(true);
      logger.info(editingRelationshipId ? 'Updating relationship friction' : 'Creating relationship friction', { relationshipForm });
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      if (editingRelationshipId) {
        // Update existing relationship friction using PUT endpoint
        const url = `${apiUrl}/analyses/${analysisId}/frictions/types/relationship/${editingRelationshipId}`;
        logger.logApiRequest('PUT', url, relationshipForm);
        
        const response = await fetch(url, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: relationshipForm.name,
            direction: relationshipForm.direction,
            magnitude: relationshipForm.magnitude,
            logicFormula: relationshipForm.logicFormula,
            regulatoryFramework: relationshipForm.regulatoryFramework || undefined,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          logger.error('Failed to update relationship friction', { status: response.status, errorData });
          throw new Error(errorData.error?.message || 'Failed to update relationship friction');
        }
        
        const result = await response.json();
        logger.logApiResponse('PUT', url, response.status, result);
        logger.logUserAction('update_relationship_friction', { 
          name: relationshipForm.name,
          appliedCount: result.appliedCount 
        });
        
        alert(`Relationship friction updated successfully! Applied to ${result.appliedCount} relationship(s).`);
      } else {
        // Create new relationship friction
        const url = `${apiUrl}/analyses/${analysisId}/frictions/types/relationship`;
        logger.logApiRequest('POST', url, relationshipForm);
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: relationshipForm.name,
            direction: relationshipForm.direction,
            magnitude: relationshipForm.magnitude,
            logicFormula: relationshipForm.logicFormula,
            regulatoryFramework: relationshipForm.regulatoryFramework || undefined,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          logger.error('Failed to create relationship friction', { status: response.status, errorData });
          throw new Error(errorData.error?.message || 'Failed to create relationship friction');
        }
        
        const result = await response.json();
        logger.logApiResponse('POST', url, response.status, result);
        logger.logUserAction('create_relationship_friction', { 
          name: relationshipForm.name,
          currentMatchCount: result.currentMatchCount 
        });
        
        const matchCount = result.currentMatchCount || 0;
        if (matchCount > 0) {
          alert(`Relationship friction created successfully! Currently applies to ${matchCount} relationship(s).`);
        } else {
          alert('Relationship friction created successfully! It will automatically apply to relationships that match the formula.');
        }
      }
      
      setRelationshipForm({
        name: '',
        direction: 'negative',
        magnitude: 5,
        logicFormula: '',
        regulatoryFramework: '',
      });
      setEditingRelationshipId(null);
      setMatchingRelationships([]);
      setShowRelationshipForm(false);
      fetchAppliedFrictions();
    } catch (err) {
      console.error('Error creating/updating relationship friction:', err);
      logger.error('Error creating/updating relationship friction', err);
      alert(err instanceof Error ? err.message : 'Failed to create/update relationship friction');
    } finally {
      setLoading(false);
    }
  };

  // Regulatory Friction Handlers
  const handleEditRegulatoryFriction = (friction: any) => {
    setRegulatoryForm({
      name: friction.name,
      direction: friction.direction || 'negative',
      magnitude: friction.magnitude || 5,
      logicFormula: friction.logicFormula || '',
      targetEntityIds: friction.targetEntityIds || [],
    });
    setEditingRegulatoryId(friction.id);
    setRegulatoryFormulaValid(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCopyRegulatoryFriction = (friction: any) => {
    setRegulatoryForm({
      name: `${friction.name} (Copy)`,
      direction: friction.direction || 'negative',
      magnitude: friction.magnitude || 5,
      logicFormula: friction.logicFormula || '',
      targetEntityIds: friction.targetEntityIds || [],
    });
    setEditingRegulatoryId(null);
    setRegulatoryFormulaValid(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelRegulatoryEdit = () => {
    setRegulatoryForm({
      name: '',
      direction: 'negative',
      magnitude: 5,
      logicFormula: '',
      targetEntityIds: [],
    });
    setEditingRegulatoryId(null);
    setMatchingRegulatoryEntities([]);
  };

  const handleCreateRegulatoryFriction = async () => {
    if (!regulatoryForm.name.trim()) {
      alert('Please enter a name for the regulatory friction');
      return;
    }

    try {
      setLoading(true);
      logger.info(editingRegulatoryId ? 'Updating regulatory friction' : 'Creating regulatory friction', { regulatoryForm });
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      if (editingRegulatoryId) {
        // Update existing regulatory friction
        const url = `${apiUrl}/analyses/${analysisId}/frictions/types/regulatory/${editingRegulatoryId}`;
        logger.logApiRequest('PUT', url, regulatoryForm);
        
        const response = await fetch(url, {
          method: 'PUT',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: regulatoryForm.name,
            direction: regulatoryForm.direction,
            magnitude: regulatoryForm.magnitude,
            logicFormula: regulatoryForm.logicFormula || undefined,
            targetEntityIds: regulatoryForm.targetEntityIds,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          logger.error('Failed to update regulatory friction', { status: response.status, errorData });
          throw new Error(errorData.error?.message || 'Failed to update regulatory friction');
        }
        
        const result = await response.json();
        logger.logApiResponse('PUT', url, response.status, result);
        logger.logUserAction('update_regulatory_friction', { name: regulatoryForm.name });
        
        alert('Regulatory friction updated successfully!');
      } else {
        // Create new regulatory friction
        const url = `${apiUrl}/analyses/${analysisId}/frictions/types/regulatory`;
        logger.logApiRequest('POST', url, regulatoryForm);
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            name: regulatoryForm.name,
            direction: regulatoryForm.direction,
            magnitude: regulatoryForm.magnitude,
            logicFormula: regulatoryForm.logicFormula || undefined,
            targetEntityIds: regulatoryForm.targetEntityIds,
          }),
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          logger.error('Failed to create regulatory friction', { status: response.status, errorData });
          throw new Error(errorData.error?.message || 'Failed to create regulatory friction');
        }
        
        const result = await response.json();
        logger.logApiResponse('POST', url, response.status, result);
        logger.logUserAction('create_regulatory_friction', { name: regulatoryForm.name });
        
        alert('Regulatory friction created successfully!');
      }
      
      setRegulatoryForm({
        name: '',
        direction: 'negative',
        magnitude: 5,
        logicFormula: '',
        targetEntityIds: [],
      });
      setEditingRegulatoryId(null);
      setMatchingRegulatoryEntities([]);
      fetchAppliedFrictions();
    } catch (err) {
      console.error('Error creating/updating regulatory friction:', err);
      logger.error('Error creating/updating regulatory friction', err);
      alert(err instanceof Error ? err.message : 'Failed to create/update regulatory friction');
    } finally {
      setLoading(false);
    }
  };

  const handleTestRegulatoryFormula = async () => {
    if (!regulatoryForm.logicFormula.trim()) {
      alert('Please enter a logic formula');
      return;
    }

    try {
      setLoading(true);
      logger.info('Testing regulatory friction formula', { formula: regulatoryForm.logicFormula });
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const url = `${apiUrl}/analyses/${analysisId}/frictions/test-formula`;
      
      logger.logApiRequest('POST', url, { formula: regulatoryForm.logicFormula, type: 'entity' });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          formula: regulatoryForm.logicFormula,
          type: 'entity'
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.error?.message || errorData.message || 'Invalid formula';
        
        if (errorData.error?.details?.evaluationErrors) {
          const errors = errorData.error.details.evaluationErrors;
          const firstError = errors[0]?.error || '';
          errorMessage = `${errorMessage}\n\nDetails: ${firstError}`;
        }
        
        logger.error('Regulatory formula test failed', null, { status: response.status, errorData });
        setRegulatoryFormulaValid(false);
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      logger.logApiResponse('POST', url, response.status, data);
      logger.info('Regulatory formula test successful', { matchCount: data.matchingEntities?.length || 0 });
      
      setRegulatoryFormulaValid(true);
      setMatchingRegulatoryEntities(data.matchingEntities || []);
      
      if (data.matchingEntities && data.matchingEntities.length > 0) {
        alert(`Formula is valid! Matches ${data.matchingEntities.length} entity(ies).`);
      } else {
        alert('Formula is valid but matches no entities.');
      }
    } catch (err) {
      console.error('Error testing regulatory formula:', err);
      logger.error('Error testing regulatory formula', err);
      setRegulatoryFormulaValid(false);
      setMatchingRegulatoryEntities([]);
      alert(err instanceof Error ? err.message : 'Invalid formula');
    } finally {
      setLoading(false);
    }
  };

  const handleTestFormula = async () => {
    if (!entityFrictionForm.logicFormula.trim()) {
      alert('Please enter a logic formula');
      return;
    }

    try {
      setLoading(true);
      logger.info('Testing entity friction formula', { formula: entityFrictionForm.logicFormula });
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const url = `${apiUrl}/analyses/${analysisId}/frictions/test-formula`;
      
      logger.logApiRequest('POST', url, { formula: entityFrictionForm.logicFormula, type: 'entity' });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          formula: entityFrictionForm.logicFormula,
          type: 'entity'
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.error?.message || errorData.message || 'Invalid formula';
        
        // If there are evaluation errors, provide more detail
        if (errorData.error?.details?.evaluationErrors) {
          const errors = errorData.error.details.evaluationErrors;
          const firstError = errors[0]?.error || '';
          errorMessage = `${errorMessage}\n\nDetails: ${firstError}`;
        }
        
        logger.error('Formula test failed', null, { status: response.status, errorData });
        setFormulaValid(false);
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      logger.logApiResponse('POST', url, response.status, data);
      logger.info('Formula test successful', { matchCount: data.matchingEntities?.length || 0 });
      
      setFormulaValid(true);
      setMatchingEntities(data.matchingEntities || []);
      
      if (data.matchingEntities && data.matchingEntities.length > 0) {
        alert(`Formula is valid! Matches ${data.matchingEntities.length} entity(ies).`);
      } else {
        alert('Formula is valid but matches no entities.');
      }
    } catch (err) {
      console.error('Error testing formula:', err);
      logger.error('Error testing formula', err);
      setFormulaValid(false);
      setMatchingEntities([]);
      alert(err instanceof Error ? err.message : 'Invalid formula');
    } finally {
      setLoading(false);
    }
  };

  const handleTestRelationshipFormula = async () => {
    if (!relationshipForm.logicFormula.trim()) {
      alert('Please enter a logic formula');
      return;
    }

    try {
      setLoading(true);
      logger.info('Testing relationship formula', { formula: relationshipForm.logicFormula });
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const url = `${apiUrl}/analyses/${analysisId}/frictions/test-formula`;
      
      logger.logApiRequest('POST', url, { 
        formula: relationshipForm.logicFormula,
        type: 'relationship'
      });
      
      const response = await fetch(url, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          formula: relationshipForm.logicFormula,
          type: 'relationship'
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        let errorMessage = errorData.error?.message || errorData.message || 'Invalid formula';
        
        // If there are evaluation errors, provide more detail
        if (errorData.error?.details?.evaluationErrors) {
          const errors = errorData.error.details.evaluationErrors;
          const firstError = errors[0]?.error || '';
          errorMessage = `${errorMessage}\n\nDetails: ${firstError}`;
        }
        
        logger.error('Relationship formula test failed', null, { status: response.status, errorData });
        setRelationshipFormulaValid(false);
        throw new Error(errorMessage);
      }
      
      const data = await response.json();
      logger.logApiResponse('POST', url, response.status, data);
      logger.info('Relationship formula test successful', { matchCount: data.matchingRelationships?.length || 0 });
      
      setRelationshipFormulaValid(true);
      setMatchingRelationships(data.matchingRelationships || []);
      
      if (data.matchingRelationships && data.matchingRelationships.length > 0) {
        alert(`Formula is valid! Matches ${data.matchingRelationships.length} relationship(s).`);
      } else {
        alert('Formula is valid but matches no relationships.');
      }
    } catch (err) {
      console.error('Error testing relationship formula:', err);
      logger.error('Error testing relationship formula', err);
      setRelationshipFormulaValid(false);
      setMatchingRelationships([]);
      alert(err instanceof Error ? err.message : 'Invalid formula');
    } finally {
      setLoading(false);
    }
  };


  const handleRemoveFriction = async (frictionId: string) => {
    if (!confirm('Are you sure you want to remove this friction?')) return;

    try {
      setLoading(true);
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/analyses/${analysisId}/frictions/${frictionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to remove friction');
      
      fetchAppliedFrictions();
    } catch (err) {
      console.error('Error removing friction:', err);
      alert(err instanceof Error ? err.message : 'Failed to remove friction');
    } finally {
      setLoading(false);
    }
  };

  const handleRefreshFrictions = async () => {
    if (!confirm('This will re-evaluate all friction formulas against current entities and relationships. Continue?')) return;

    try {
      setRefreshing(true);
      logger.info('Refreshing all frictions', { analysisId });
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        logger.error('No authentication token available');
        throw new Error('Not authenticated');
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const url = `${apiUrl}/analyses/${analysisId}/frictions/refresh`;
      
      logger.logApiRequest('POST', url);
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      logger.logApiResponse('POST', url, response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        logger.error('Failed to refresh frictions', null, { 
          status: response.status, 
          statusText: response.statusText,
          body: errorText
        });
        throw new Error(`Failed to refresh frictions: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.info('Frictions refreshed successfully', { 
        totalFrictions: data.totalFrictions,
        updatedCount: data.updatedCount 
      });
      
      alert(`Frictions refreshed! ${data.updatedCount} of ${data.totalFrictions} frictions were updated.`);
      
      // Reload the frictions list
      fetchAppliedFrictions();
    } catch (err) {
      logger.error('Error refreshing frictions', err);
      alert(err instanceof Error ? err.message : 'Failed to refresh frictions');
    } finally {
      setRefreshing(false);
    }
  };

  const validateFormula = (formula: string): boolean => {
    // Basic syntax validation
    const validOperators = ['AND', 'OR', 'NOT', '==', '!=', 'contains', '>', '<', '>=', '<='];
    const hasValidSyntax = validOperators.some(op => formula.includes(op));
    return hasValidSyntax || formula.trim().length === 0;
  };

  const fetchAffectedEntities = async (frictionId: string) => {
    // Check cache first - only skip if we have actual items, not just count
    const cached = affectedItemsCache.get(frictionId);
    if (cached && cached.entities && cached.entities.length > 0) {
      return;
    }

    try {
      setLoadingAffectedItems(prev => new Set(prev).add(frictionId));
      logger.info('Fetching affected entities', { frictionId });
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const url = `${apiUrl}/analyses/${analysisId}/frictions/${frictionId}/affected-entities`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch affected entities: ${response.status}`);
      }
      
      const data = await response.json();
      logger.info('Affected entities fetched', { count: data.count });
      
      // Convert to ExpandableListItem format
      const items: ExpandableListItem[] = data.entities.map((entity: any) => ({
        id: entity.id,
        displayName: entity.name,
        attributes: {
          jurisdiction: entity.entityJurisdiction,
          dataTypes: entity.dataTypes.join(', ') || 'None',
          categories: entity.regulatoryCategories.join(', ') || 'None'
        }
      }));
      
      // Update cache
      setAffectedItemsCache(prev => new Map(prev).set(frictionId, { entities: items, count: data.count }));
    } catch (err) {
      logger.error('Error fetching affected entities', err);
    } finally {
      setLoadingAffectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(frictionId);
        return newSet;
      });
    }
  };

  const fetchAffectedRelationships = async (frictionId: string) => {
    // Check cache first - only skip if we have actual items, not just count
    const cached = affectedItemsCache.get(frictionId);
    if (cached && cached.relationships && cached.relationships.length > 0) {
      return;
    }

    try {
      setLoadingAffectedItems(prev => new Set(prev).add(frictionId));
      logger.info('Fetching affected relationships', { frictionId });
      
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        throw new Error('Not authenticated');
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      const url = `${apiUrl}/analyses/${analysisId}/frictions/${frictionId}/affected-relationships`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch affected relationships: ${response.status}`);
      }
      
      const data = await response.json();
      logger.info('Affected relationships fetched', { count: data.count });
      
      // Convert to ExpandableListItem format
      const items: ExpandableListItem[] = data.relationships.map((rel: any) => ({
        id: rel.id,
        displayName: `${rel.sourceEntityName} → ${rel.targetEntityName}`,
        attributes: {
          type: rel.relationshipType
        }
      }));
      
      // Update cache
      setAffectedItemsCache(prev => new Map(prev).set(frictionId, { relationships: items, count: data.count }));
    } catch (err) {
      logger.error('Error fetching affected relationships', err);
    } finally {
      setLoadingAffectedItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(frictionId);
        return newSet;
      });
    }
  };

  const fetchAllFrictionCounts = async () => {
    try {
      logger.info('Pre-fetching counts for all frictions');
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();
      
      if (!token) {
        return;
      }
      
      const apiUrl = import.meta.env?.VITE_API_ENDPOINT || 'http://localhost:3000';
      
      // Fetch counts for all frictions in parallel
      const countPromises = appliedFrictions.map(async (friction) => {
        try {
          const endpoint = friction.frictionType === 'relationship_friction'
            ? `${apiUrl}/analyses/${analysisId}/frictions/${friction.id}/affected-relationships`
            : `${apiUrl}/analyses/${analysisId}/frictions/${friction.id}/affected-entities`;
          
          const response = await fetch(endpoint, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            return { frictionId: friction.id, count: data.count };
          }
          return { frictionId: friction.id, count: 0 };
        } catch (err) {
          logger.warn('Error fetching count for friction', { frictionId: friction.id, error: err });
          return { frictionId: friction.id, count: 0 };
        }
      });
      
      const counts = await Promise.all(countPromises);
      
      // Update cache with counts only (don't fetch full data yet)
      const newCache = new Map(affectedItemsCache);
      counts.forEach(({ frictionId, count }) => {
        if (!newCache.has(frictionId)) {
          newCache.set(frictionId, { count });
        }
      });
      setAffectedItemsCache(newCache);
      
      logger.info('Pre-fetched counts for all frictions', { totalFrictions: counts.length });
    } catch (err) {
      logger.error('Error pre-fetching friction counts', err);
    }
  };

  const handleNavigateToEntity = (entityId: string) => {
    logger.logUserAction('Navigate to entity from expandable list', { entityId });
    // Dispatch custom event to switch to visualization tab and highlight entity
    window.dispatchEvent(new CustomEvent('navigateToEntity', { detail: { entityId } }));
  };

  const handleNavigateToRelationship = (relationshipId: string) => {
    logger.logUserAction('Navigate to relationship from expandable list', { relationshipId });
    // Dispatch custom event to switch to visualization tab and highlight relationship
    window.dispatchEvent(new CustomEvent('navigateToRelationship', { detail: { relationshipId } }));
  };

  return (
    <div className="friction-management-ui">
      <h3 style={{ marginBottom: '16px' }}>Friction Management</h3>

      {error && (
        <div style={{ padding: '12px', background: '#fee', border: '1px solid #fcc', borderRadius: '4px', marginBottom: '16px', color: '#c00' }}>
          {error}
        </div>
      )}

      {/* Tabs */}
      <div className="friction-tabs" style={{ display: 'flex', gap: '8px', marginBottom: '16px', borderBottom: '1px solid #d1d5db', paddingBottom: '8px' }}>
        <button
          onClick={() => setActiveTab('framework')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'framework' ? '#3b82f6' : 'transparent',
            color: activeTab === 'framework' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: activeTab === 'framework' ? '600' : '400',
          }}
        >
          📊 Framework View
        </button>
        <button
          onClick={() => setActiveTab('entity')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'entity' ? '#3b82f6' : 'transparent',
            color: activeTab === 'entity' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: activeTab === 'entity' ? '600' : '400',
          }}
        >
          Entity Friction
        </button>
        <button
          onClick={() => setActiveTab('relationship')}
          style={{
            padding: '8px 16px',
            background: activeTab === 'relationship' ? '#3b82f6' : 'transparent',
            color: activeTab === 'relationship' ? 'white' : '#333',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: activeTab === 'relationship' ? '600' : '400',
          }}
        >
          Relationship Friction
        </button>
      </div>

      {/* Framework View Tab */}
      {activeTab === 'framework' && (
        <div className="framework-view-tab">
          <div style={{
            padding: '16px',
            background: '#eff6ff',
            border: '1px solid #bfdbfe',
            borderRadius: '8px',
            marginBottom: '16px'
          }}>
            <h4 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '15px' }}>
              📊 Framework-Grouped View
            </h4>
            <p style={{ margin: 0, fontSize: '13px', color: '#1e3a8a', lineHeight: '1.5' }}>
              View all frictions organized by regulatory framework. Entity and relationship frictions are grouped together under their respective frameworks. Use filters and search to find specific frictions.
            </p>
          </div>

          <FrictionListView
            frictions={appliedFrictions
              .filter(f => f.regulatoryFramework) // Only show frictions WITH framework tags
              .map(f => ({
                ...f,
                frictionType: f.frictionType as 'entity_friction' | 'relationship_friction' | 'regulatory_friction'
              }))}
            affectedItemsCache={affectedItemsCache}
            loadingAffectedItems={loadingAffectedItems}
            onEditFriction={(friction) => {
              if (friction.frictionType === 'relationship_friction') {
                handleEditRelationshipFriction(friction);
                setActiveTab('relationship');
              } else {
                // Both entity_friction and regulatory_friction (legacy) go to entity tab
                handleEditEntityFriction(friction);
                setActiveTab('entity');
              }
            }}
            onCopyFriction={(friction) => {
              if (friction.frictionType === 'relationship_friction') {
                handleCopyRelationshipFriction(friction);
                setActiveTab('relationship');
              } else {
                // Both entity_friction and regulatory_friction (legacy) go to entity tab
                handleCopyEntityFriction(friction);
                setActiveTab('entity');
              }
            }}
            onRemoveFriction={handleRemoveFriction}
            onNavigateToEntity={handleNavigateToEntity}
            onNavigateToRelationship={handleNavigateToRelationship}
            onFetchAffectedEntities={fetchAffectedEntities}
            onFetchAffectedRelationships={fetchAffectedRelationships}
            onAddFriction={(framework) => {
              // Switch to appropriate tab based on context
              // For now, default to entity friction
              setActiveTab('entity');
              // Could pre-populate framework in the form here
            }}
          />
        </div>
      )}

      {/* Entity Friction Tab (formerly Sentiment Bias) */}
      {activeTab === 'entity' && (
        <div className="entity-friction-tab">
          {/* Add/Cancel Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Entity Frictions</h3>
            <button
              onClick={() => {
                if (showEntityForm) {
                  handleCancelEdit();
                } else {
                  setShowEntityForm(true);
                }
              }}
              style={{
                padding: '8px 16px',
                background: showEntityForm ? '#ef4444' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {showEntityForm ? 'Cancel' : '+ Add Entity Friction'}
            </button>
          </div>

          {/* Collapsible Form */}
          {showEntityForm && (
        <div className="sentiment-bias-tab" style={{
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          background: '#fafafa',
        }}>
          <h4 style={{ marginTop: 0 }}>{editingEntityFrictionId ? 'Edit Entity Friction' : 'Create Entity Friction'}</h4>
          
          {/* Help Guide - Collapsible */}
          <details style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            background: '#eff6ff', 
            border: '1px solid #bfdbfe', 
            borderRadius: '6px' 
          }}>
            <summary style={{ 
              cursor: 'pointer', 
              fontWeight: '600', 
              color: '#1e40af',
              marginBottom: '8px'
            }}>
              📖 Formula Writing Guide
            </summary>
            
            <div style={{ fontSize: '13px', lineHeight: '1.6', color: '#1e3a8a' }}>
              <p style={{ marginTop: '8px', marginBottom: '12px' }}>
                Logic formulas allow you to target entities based on their attributes. Use boolean logic to create complex conditions.
              </p>

              <div style={{ marginBottom: '12px' }}>
                <strong>Available Entity Attributes:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                  <li><code>entityJurisdiction</code> - Entity's headquarters location (e.g., 'China', 'US', 'EU')</li>
                  <li><code>name</code> - Entity's name</li>
                  <li><code>dataTypes</code> - Array of data types processed (e.g., 'PII', 'HEALTH_DATA')</li>
                  <li><code>naicsCodes</code> - Array of NAICS industry codes</li>
                  <li><code>regulatoryCategories</code> - Array of regulatory categories</li>
                  <li><code>customAttributes.*</code> - Any custom attributes you've defined (e.g., customAttributes.businessModel, customAttributes.is_as_a_service)</li>
                </ul>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <strong>Supported Operators:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                  <li><code>==</code> - Equals (exact match)</li>
                  <li><code>!=</code> - Not equals</li>
                  <li><code>contains</code> - Array/string contains value</li>
                  <li><code>{'>'}</code>, <code>{'<'}</code>, <code>{'>='}</code>, <code>{'<='}</code> - Numeric comparisons</li>
                  <li><code>AND</code> - Both conditions must be true</li>
                  <li><code>OR</code> - Either condition must be true</li>
                  <li><code>NOT</code> - Negates a condition</li>
                  <li><code>( )</code> - Parentheses for grouping</li>
                </ul>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <strong>Example Formulas:</strong>
                <div style={{ 
                  background: '#1e3a8a', 
                  color: '#e0e7ff', 
                  padding: '8px', 
                  borderRadius: '4px', 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Target entities in China:</div>
                    <div>entityJurisdiction == 'China'</div>
                  </div>
                  
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Target cloud providers in China:</div>
                    <div>entityJurisdiction == 'China' AND customAttributes.businessModel == 'cloud_provider'</div>
                  </div>
                  
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Target entities processing government data:</div>
                    <div>dataTypes contains 'GOVERNMENT_DATA'</div>
                  </div>
                  
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Target financial institutions in EU or US:</div>
                    <div>regulatoryCategories contains 'FINANCIAL_INSTITUTION' AND (entityJurisdiction == 'EU' OR entityJurisdiction == 'US')</div>
                  </div>
                  
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Target large entities (custom attribute):</div>
                    <div>customAttributes.revenue {'>'} 1000000000</div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Exclude specific entity:</div>
                    <div>NOT (name == 'Trusted Partner Inc')</div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <strong>Tips:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                  <li>Use single or double quotes for string values: 'China' or "China"</li>
                  <li>String comparisons are case-sensitive</li>
                  <li>Use <code>contains</code> for arrays (dataTypes, naicsCodes, regulatoryCategories)</li>
                  <li>Custom attributes must use the <code>customAttributes.</code> prefix (e.g., customAttributes.businessModel)</li>
                  <li>Test your formula before creating to see which entities match</li>
                </ul>
              </div>
            </div>
          </details>
          
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Name:
            </label>
            <input
              type="text"
              value={entityFrictionForm.name}
              onChange={(e) => setEntityFrictionForm({ ...entityFrictionForm, name: e.target.value })}
              placeholder="e.g., Anti-China Bias"
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>

          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Regulatory Framework (Optional):
            </label>
            <input
              type="text"
              list="entity-frameworks-list"
              value={entityFrictionForm.regulatoryFramework}
              onChange={(e) => setEntityFrictionForm({ ...entityFrictionForm, regulatoryFramework: e.target.value })}
              placeholder="Type to create new or select existing..."
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
            <datalist id="entity-frameworks-list">
              {availableFrameworks.map(fw => (
                <option key={fw} value={fw} />
              ))}
            </datalist>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {availableFrameworks.length > 0 
                ? 'Select an existing framework or type a new one. Use descriptive names like "GDPR" or "Canadian Privacy Laws".'
                : 'Type a descriptive framework name (e.g., "GDPR", "NIS 2", "Canadian Privacy Laws")'}
            </div>
          </div>

          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Direction:
            </label>
            <select
              value={entityFrictionForm.direction}
              onChange={(e) => setEntityFrictionForm({ ...entityFrictionForm, direction: e.target.value as 'negative' | 'positive' })}
              style={{ width: '100%', padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            >
              <option value="negative">Negative (increases tension)</option>
              <option value="positive">Positive (decreases tension)</option>
            </select>
          </div>

          {/* Mode Toggle */}
          <div style={{ marginBottom: '16px', padding: '12px', background: '#f3f4f6', borderRadius: '6px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: '600', fontSize: '14px' }}>Configuration Mode:</span>
              <button
                type="button"
                onClick={() => {
                  if (entityFrictionForm.mode === 'simple') {
                    // Switching to advanced: preserve current values
                    setEntityFrictionForm({
                      ...entityFrictionForm,
                      mode: 'advanced',
                      baselineTension: entityFrictionForm.magnitude,
                      multiplier: 1.0 + (entityFrictionForm.magnitude / 10)
                    });
                  } else {
                    // Switching to simple: calculate magnitude from baseline
                    setEntityFrictionForm({
                      ...entityFrictionForm,
                      mode: 'simple',
                      magnitude: entityFrictionForm.baselineTension
                    });
                  }
                }}
                style={{
                  padding: '6px 12px',
                  background: '#3b82f6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                }}
              >
                Switch to {entityFrictionForm.mode === 'simple' ? 'Advanced' : 'Simple'} Mode
              </button>
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {entityFrictionForm.mode === 'simple' 
                ? '📊 Simple Mode: Single slider controls both baseline tension and multiplier'
                : '⚙️ Advanced Mode: Independent control of baseline tension and multiplier'}
            </div>
          </div>

          {/* Simple Mode Controls */}
          {entityFrictionForm.mode === 'simple' && (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '6px' }}>
              <div className="form-field" style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                  Magnitude: {entityFrictionForm.magnitude.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.5"
                  value={entityFrictionForm.magnitude}
                  onChange={(e) => setEntityFrictionForm({ ...entityFrictionForm, magnitude: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />
              </div>
              
              <div style={{ 
                padding: '8px', 
                background: 'white', 
                borderRadius: '4px', 
                fontSize: '13px',
                border: '1px solid #bfdbfe'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '4px', color: '#1e40af' }}>Calculated Values:</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                  <div>
                    <span style={{ color: '#666' }}>Baseline Tension:</span>
                    <span style={{ fontWeight: '600', marginLeft: '4px', color: '#1e40af' }}>
                      {entityFrictionForm.magnitude.toFixed(1)}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#666' }}>Multiplier:</span>
                    <span style={{ fontWeight: '600', marginLeft: '4px', color: '#1e40af' }}>
                      {(1.0 + entityFrictionForm.magnitude / 10).toFixed(1)}x
                    </span>
                  </div>
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                  💡 Creates a tension floor of {entityFrictionForm.magnitude.toFixed(1)} and amplifies by {(1.0 + entityFrictionForm.magnitude / 10).toFixed(1)}x
                </div>
              </div>
            </div>
          )}

          {/* Advanced Mode Controls */}
          {entityFrictionForm.mode === 'advanced' && (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: '6px' }}>
              <div className="form-field" style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                  Baseline Tension: {entityFrictionForm.baselineTension.toFixed(1)}
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.5"
                  value={entityFrictionForm.baselineTension}
                  onChange={(e) => setEntityFrictionForm({ ...entityFrictionForm, baselineTension: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Sets the minimum tension level (additive floor)
                </div>
              </div>
              
              <div className="form-field" style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
                  Multiplier (1.0-2.0): {entityFrictionForm.multiplier.toFixed(1)}x
                </label>
                <input
                  type="range"
                  min="1.0"
                  max="2.0"
                  step="0.1"
                  value={entityFrictionForm.multiplier}
                  onChange={(e) => setEntityFrictionForm({ ...entityFrictionForm, multiplier: parseFloat(e.target.value) })}
                  style={{ width: '100%' }}
                />
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Amplifies existing tension (multiplicative factor)
                </div>
              </div>
              
              <div style={{ 
                padding: '8px', 
                background: 'white', 
                borderRadius: '4px', 
                fontSize: '12px',
                border: '1px solid #fcd34d',
                color: '#92400e'
              }}>
                <strong>Example:</strong> If current tension is 8.0, this friction will set it to max(8.0, {entityFrictionForm.baselineTension.toFixed(1)}) × {entityFrictionForm.multiplier.toFixed(1)} = {(Math.max(8.0, entityFrictionForm.baselineTension) * entityFrictionForm.multiplier).toFixed(1)}
              </div>
            </div>
          )}

          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '14px' }}>
              Logic Formula:
            </label>
            <textarea
              value={entityFrictionForm.logicFormula}
              onChange={(e) => {
                setEntityFrictionForm({ ...entityFrictionForm, logicFormula: e.target.value });
                setFormulaValid(validateFormula(e.target.value));
              }}
              placeholder="e.g., entityJurisdiction == 'China' AND customAttributes.businessModel == 'cloud_provider'"
              rows={4}
              style={{
                width: '100%',
                padding: '8px',
                border: `1px solid ${formulaValid ? '#d1d5db' : '#ef4444'}`,
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '13px',
              }}
            />
            {!formulaValid && entityFrictionForm.logicFormula.trim() && (
              <div style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                ⚠️ Invalid formula syntax. Check the guide above for help.
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button
              onClick={handleTestFormula}
              disabled={loading}
              style={{
                padding: '8px 16px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Testing...' : 'Test Formula'}
            </button>
            <button
              onClick={handleCreateEntityFriction}
              disabled={loading || !formulaValid}
              style={{
                padding: '8px 16px',
                background: '#22c55e',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || !formulaValid ? 'not-allowed' : 'pointer',
                fontWeight: '500',
              }}
            >
              {loading ? (editingEntityFrictionId ? 'Updating...' : 'Creating...') : (editingEntityFrictionId ? 'Update Entity Friction' : 'Create Entity Friction')}
            </button>
            {editingEntityFrictionId && (
              <button
                onClick={handleCancelEdit}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
            )}
          </div>

          {matchingEntities.length > 0 && (
            <div style={{ padding: '12px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: '4px' }}>
              <p style={{ fontWeight: '500', marginBottom: '8px' }}>
                Matching Entities ({matchingEntities.length}):
              </p>
              <div style={{ fontSize: '13px' }}>
                {matchingEntities.map((entity, idx) => (
                  <div key={idx} style={{ marginBottom: '4px' }}>• {entity.name}</div>
                ))}
              </div>
            </div>
          )}
        </div>
          )}

          {/* Existing Entity Frictions List - OUTSIDE form container */}
          {appliedFrictions.filter(p => p.frictionType === 'entity_friction').length > 0 && (
            <div>
              <FrictionListView
                frictions={appliedFrictions
                  .filter(f => f.frictionType === 'entity_friction')
                  .map(f => ({
                    ...f,
                    frictionType: f.frictionType as 'entity_friction' | 'relationship_friction' | 'regulatory_friction'
                  }))}
                affectedItemsCache={affectedItemsCache}
                loadingAffectedItems={loadingAffectedItems}
                onEditFriction={handleEditEntityFriction}
                onCopyFriction={handleCopyEntityFriction}
                onRemoveFriction={handleRemoveFriction}
                onNavigateToEntity={handleNavigateToEntity}
                onNavigateToRelationship={handleNavigateToRelationship}
                onFetchAffectedEntities={fetchAffectedEntities}
                onFetchAffectedRelationships={fetchAffectedRelationships}
                initialFrictionTypeFilter="entity"
              />
            </div>
          )}
        </div>
      )}

      {/* Relationship Friction Tab */}
      {activeTab === 'relationship' && (
        <div className="relationship-friction-tab">
          {/* Add/Cancel Button */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h3 style={{ margin: 0 }}>Relationship Frictions</h3>
            <button
              onClick={() => {
                if (showRelationshipForm) {
                  setRelationshipForm({
                    name: '',
                    direction: 'negative',
                    magnitude: 5,
                    logicFormula: '',
                    regulatoryFramework: '',
                  });
                  setEditingRelationshipId(null);
                  setMatchingRelationships([]);
                  setShowRelationshipForm(false);
                } else {
                  setShowRelationshipForm(true);
                }
              }}
              style={{
                padding: '8px 16px',
                background: showRelationshipForm ? '#ef4444' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
              }}
            >
              {showRelationshipForm ? 'Cancel' : '+ Add Relationship Friction'}
            </button>
          </div>

          {/* Collapsible Form */}
          {showRelationshipForm && (
        <div style={{
          border: '1px solid #d1d5db',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          background: '#fafafa',
        }}>
          <h4 style={{ marginTop: 0 }}>{editingRelationshipId ? 'Edit Relationship Friction' : 'Create Relationship Friction'}</h4>
          
          {/* Help Guide - Collapsible */}
          <details style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            background: '#eff6ff', 
            border: '1px solid #bfdbfe', 
            borderRadius: '6px' 
          }}>
            <summary style={{ 
              cursor: 'pointer', 
              fontWeight: '600', 
              color: '#1e40af',
              marginBottom: '8px'
            }}>
              📖 Relationship Formula Writing Guide
            </summary>
            
            <div style={{ fontSize: '13px', lineHeight: '1.6', color: '#1e3a8a' }}>
              <p style={{ marginTop: '8px', marginBottom: '12px' }}>
                Relationship formulas allow you to target relationships based on source entity, target entity, and relationship attributes.
              </p>

              <div style={{ marginBottom: '12px' }}>
                <strong>Available Attributes:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                  <li><strong>Source Entity:</strong> <code>source.name</code>, <code>source.entityJurisdiction</code>, <code>source.dataTypes</code>, <code>source.naicsCodes</code>, <code>source.regulatoryCategories</code>, <code>source.customAttributes.*</code></li>
                  <li><strong>Target Entity:</strong> <code>target.name</code>, <code>target.entityJurisdiction</code>, <code>target.dataTypes</code>, <code>target.naicsCodes</code>, <code>target.regulatoryCategories</code>, <code>target.customAttributes.*</code></li>
                  <li><strong>Relationship:</strong> <code>relationship.relationshipType</code>, <code>relationship.financialImpact</code></li>
                </ul>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <strong>Supported Operators:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                  <li><code>==</code> - Equals (exact match)</li>
                  <li><code>!=</code> - Not equals</li>
                  <li><code>contains</code> - Array/string contains value</li>
                  <li><code>{'>'}</code>, <code>{'<'}</code>, <code>{'>='}</code>, <code>{'<='}</code> - Numeric comparisons</li>
                  <li><code>AND</code> - Both conditions must be true</li>
                  <li><code>OR</code> - Either condition must be true</li>
                  <li><code>NOT</code> - Negates a condition</li>
                  <li><code>( )</code> - Parentheses for grouping</li>
                </ul>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <strong>Example Formulas:</strong>
                <div style={{ 
                  background: '#1e3a8a', 
                  color: '#e0e7ff', 
                  padding: '8px', 
                  borderRadius: '4px', 
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  marginTop: '4px'
                }}>
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Cross-border data flows from EU to US:</div>
                    <div>source.entityJurisdiction == 'EU' AND target.entityJurisdiction == 'US'</div>
                  </div>
                  
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Data flows to cloud providers:</div>
                    <div>target.customAttributes.businessModel == 'cloud_provider'</div>
                  </div>
                  
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// PII data flows:</div>
                    <div>source.dataTypes contains 'PII' AND relationship.relationshipType == 'data_flow'</div>
                  </div>
                  
                  <div style={{ marginBottom: '6px' }}>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Flows from China to any jurisdiction:</div>
                    <div>source.entityJurisdiction == 'China'</div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#93c5fd', marginBottom: '2px' }}>// Financial data between financial institutions:</div>
                    <div>source.regulatoryCategories contains 'FINANCIAL_INSTITUTION' AND target.regulatoryCategories contains 'FINANCIAL_INSTITUTION'</div>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <strong>Tips:</strong>
                <ul style={{ marginLeft: '20px', marginTop: '4px' }}>
                  <li>Always use <code>source.</code>, <code>target.</code>, or <code>relationship.</code> prefix</li>
                  <li>Use single or double quotes for string values</li>
                  <li>String comparisons are case-sensitive</li>
                  <li>Use <code>contains</code> for arrays</li>
                  <li>Custom attributes must use the <code>customAttributes.</code> prefix (e.g., source.customAttributes.businessModel)</li>
                  <li>Test your formula before creating to see which relationships match</li>
                </ul>
              </div>
            </div>
          </details>
          
          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Friction Name
            </label>
            <input
              type="text"
              value={relationshipForm.name}
              onChange={(e) => setRelationshipForm({ ...relationshipForm, name: e.target.value })}
              placeholder="e.g., Cross-Border Data Transfer Tension"
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
              }}
            />
          </div>

          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Regulatory Framework (Optional)
            </label>
            <input
              type="text"
              list="relationship-frameworks-list"
              value={relationshipForm.regulatoryFramework}
              onChange={(e) => setRelationshipForm({ ...relationshipForm, regulatoryFramework: e.target.value })}
              placeholder="Type to create new or select existing..."
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
              }}
            />
            <datalist id="relationship-frameworks-list">
              {availableFrameworks.map(fw => (
                <option key={fw} value={fw} />
              ))}
            </datalist>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              {availableFrameworks.length > 0 
                ? 'Select an existing framework or type a new one. Use descriptive names like "GDPR" or "Canadian Privacy Laws".'
                : 'Type a descriptive framework name (e.g., "GDPR", "NIS 2", "Canadian Privacy Laws")'}
            </div>
          </div>

          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Direction
            </label>
            <select
              value={relationshipForm.direction}
              onChange={(e) => setRelationshipForm({ ...relationshipForm, direction: e.target.value as 'positive' | 'negative' })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
              }}
            >
              <option value="negative">Negative (Increases Tension)</option>
              <option value="positive">Positive (Reduces Tension)</option>
            </select>
          </div>

          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Magnitude
            </label>
            <input
              type="number"
              min="0"
              step="0.5"
              value={relationshipForm.magnitude}
              onChange={(e) => setRelationshipForm({ ...relationshipForm, magnitude: parseFloat(e.target.value) })}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
              }}
            />
          </div>

          <div className="form-field" style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Logic Formula
            </label>
            <textarea
              value={relationshipForm.logicFormula}
              onChange={(e) => {
                setRelationshipForm({ ...relationshipForm, logicFormula: e.target.value });
                setRelationshipFormulaValid(true);
              }}
              placeholder="e.g., source.entityJurisdiction == 'EU' AND target.entityJurisdiction == 'US'"
              rows={3}
              style={{
                width: '100%',
                padding: '8px',
                border: `1px solid ${relationshipFormulaValid ? '#d1d5db' : '#f87171'}`,
                borderRadius: '4px',
                fontFamily: 'monospace',
                fontSize: '14px',
              }}
            />
            {!relationshipFormulaValid && (
              <div style={{ color: '#dc2626', fontSize: '14px', marginTop: '4px' }}>
                Invalid formula syntax
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button
              onClick={handleTestRelationshipFormula}
              disabled={loading || !relationshipForm.logicFormula}
              style={{
                padding: '10px 20px',
                background: loading || !relationshipForm.logicFormula ? '#d1d5db' : '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || !relationshipForm.logicFormula ? 'not-allowed' : 'pointer',
                fontWeight: '600',
              }}
            >
              {loading ? 'Testing...' : 'Test Formula'}
            </button>
            <button
              onClick={handleCreateRelationshipFriction}
              disabled={loading || !relationshipForm.name || !relationshipForm.logicFormula}
              style={{
                padding: '10px 20px',
                background: loading || !relationshipForm.name || !relationshipForm.logicFormula ? '#d1d5db' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: loading || !relationshipForm.name || !relationshipForm.logicFormula ? 'not-allowed' : 'pointer',
                fontWeight: '600',
              }}
            >
              {loading ? (editingRelationshipId ? 'Updating...' : 'Creating...') : (editingRelationshipId ? 'Update Relationship Friction' : 'Create Relationship Friction')}
            </button>
            {editingRelationshipId && (
              <button
                onClick={handleCancelRelationshipEdit}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                }}
              >
                Cancel
              </button>
            )}
          </div>

          {matchingRelationships.length > 0 && (
            <div style={{ marginBottom: '16px', padding: '12px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: '4px' }}>
              <strong>Matching Relationships: {matchingRelationships.length}</strong>
              <ul style={{ marginTop: '8px', marginBottom: 0 }}>
                {matchingRelationships.slice(0, 5).map((rel: any) => (
                  <li key={rel.id}>{rel.source} → {rel.target} ({rel.type})</li>
                ))}
                {matchingRelationships.length > 5 && (
                  <li>... and {matchingRelationships.length - 5} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
          )}

          {/* Existing Relationship Frictions List - OUTSIDE form container */}
          {appliedFrictions.filter(p => p.frictionType === 'relationship_friction').length > 0 && (
            <div>
              <FrictionListView
                frictions={appliedFrictions
                  .filter(f => f.frictionType === 'relationship_friction')
                  .map(f => ({
                    ...f,
                    frictionType: f.frictionType as 'entity_friction' | 'relationship_friction' | 'regulatory_friction'
                  }))}
                affectedItemsCache={affectedItemsCache}
                loadingAffectedItems={loadingAffectedItems}
                onEditFriction={handleEditRelationshipFriction}
                onCopyFriction={handleCopyRelationshipFriction}
                onRemoveFriction={handleRemoveFriction}
                onNavigateToEntity={handleNavigateToEntity}
                onNavigateToRelationship={handleNavigateToRelationship}
                onFetchAffectedEntities={fetchAffectedEntities}
                onFetchAffectedRelationships={fetchAffectedRelationships}
                initialFrictionTypeFilter="relationship"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
});
